import { Component, Element, Event, type EventEmitter, h, Host, Listen, Prop, State } from '@stencil/core';

@Component({
  tag: 'baywa-select-options',
  styleUrl: 'baywa-select-options.scss',
  scoped: true,
})
export class BaywaSelectOptions {
  private optionListEl: HTMLUListElement;
  private inputEl: HTMLInputElement;
  private selectedItem: HTMLLIElement;

  @Element() el: HTMLElement;

  /**
   * The internal representation of the options property
   */
  @State() optionsState: HTMLOptionElement[] = [];
  /**
   * The search input regexp
   */
  @State() searchString: string = '';
  /**
   * The list index of the selected item
   */
  @State() selectedItemIndex: number = -1;

  /**
   * The index of the option to highlight
   */
  @Prop() highlight: number = -1;
  /**
   * Only if a placeholder is used, offset the index.
   */
  @Prop() indexOffset: number = 0;
  /**
   * Allow multi select
   */
  @Prop() multiple: boolean = false;
  /**
   * The element reference that triggered the list open.
   */
  @Prop() opener: HTMLElement;
  /**
   * The list of assigned options
   */
  @Prop() options: HTMLOptionsCollection;
  /**
   * Allow search
   */
  @Prop() searchable: boolean = false;

  /**
   * Fired when an option has been selected
   */
  @Event() optionSelected: EventEmitter<void>;
  /**
   * Fired when list is about to close
   */
  @Event() listClose: EventEmitter<void>;

  componentWillLoad() {
    this.optionsState = Array.from(this.options);
    if (this.highlight > -1) this.selectedItemIndex = this.highlight;
  }

  componentDidLoad() {
    this.searchable ? this.inputEl?.focus() : this.optionListEl?.focus();
  }

  /**
   * Listens for all clicks. If not clicked inside the component or the trigger
   * component, close this component.
   *
   * @param event The captured mouse click
   */
  @Listen('click', { target: 'document' })
  handleDocumentClick({ target: clickTarget }: Event) {
    const hasClickedOutside = clickTarget !== this.opener && clickTarget !== this.el && !this.el.contains(clickTarget as HTMLElement);
    if (hasClickedOutside) {
      this.closeList(true);
    }
  }

  /**
   * Listens to the keyboard events and reacts according to the key pressed
   * * ArrowUp moves the selection up one, cycling to the bottom
   * * ArrowDown moves the selection down one, cycling to the top
   * * Enter selects the highlighted item
   * * Escape closes the menu
   *
   * @param keyEvent The pressed key from KeyboardEvent.
   */
  @Listen('keydown', { target: 'document', capture: true })
  handleKeyDown(keyEvent: KeyboardEvent) {
    const childNodes = this.optionListEl.querySelectorAll('li');
    const itemCount = childNodes.length;

    childNodes.forEach(childNode => childNode.classList.remove('focus'));
    switch (keyEvent.key) {
      case 'ArrowUp':
        this.selectedItemIndex--;
        if (this.selectedItemIndex < 0) {
          this.selectedItemIndex = itemCount - 1;
        }
        break;
      case 'ArrowDown': {
        this.selectedItemIndex++;
        if (this.selectedItemIndex >= itemCount) {
          this.selectedItemIndex = 0;
        }
        break;
      }
      case 'Enter': {
        this.confirmSelection();
        return;
      }
      case 'Escape':
        this.closeList(true);
        return;
      default:
        return;
    }

    keyEvent.stopPropagation();
    keyEvent.preventDefault();

    const selectedItem = childNodes[this.selectedItemIndex];
    selectedItem.classList.add('focus');
    selectedItem.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest',
    });
  }

  private closeList(forceClose: boolean = false) {
    if (!this.multiple || forceClose) {
      this.listClose.emit();
    }
  }

  private confirmSelection() {
    this.selectedItem?.click();
  }

  private filterOption(option: HTMLOptionElement): boolean {
    if (option.hidden) {
      return false;
    }
    return this.searchString ? option.text.toLowerCase().includes(this.searchString) : true;
  }

  private handleFilter() {
    this.searchString = this.inputEl.value.toLowerCase();
  }

  private isOptionGroup(option: HTMLElement) {
    return option?.tagName === 'OPTGROUP';
  }

  private selectOption(option: HTMLOptionElement) {
    if (this.multiple) {
      option.selected = !option.selected;
    } else {
      option.selected = true;
    }

    // re-map state-list in order to trigger a re-render action
    this.optionsState = this.optionsState.map(opt => opt);
    this.optionSelected.emit();
    this.closeList();
  }

  /**
   * Renders an option list entry with optional icon.
   *
   * @param option The select option reference
   * @param optionIndex The list index
   */
  private renderOptionItem(option: HTMLOptionElement, optionIndex: number) {
    const parentEl = option.parentElement;
    if (parentEl) {
      const isOptionGroup = this.isOptionGroup(parentEl);
      const children = isOptionGroup ? Array.from(parentEl.children) : [];
      const childIdx = children.indexOf(option);
      const isOptGroupStart = isOptionGroup && childIdx === 0;
      const isOptGroupEnd = isOptionGroup && childIdx === children.length - 1;
      const isSelected = optionIndex === this.selectedItemIndex;
      return (
        <li
          class={{
            'options__item': true,
            'highlight': !this.multiple && option.selected,
            'checked': this.multiple && option.selected,
            'disabled': option.disabled,
            'group-start': isOptGroupStart,
            'group-end': isOptGroupEnd,
          }}
          key={optionIndex}
          ref={(el: HTMLLIElement) => (isSelected ? (this.selectedItem = el) : null)}
          onClick={() => {
            !option.disabled && this.selectOption(option);
          }}
        >
          {this.multiple ? <i class="select-checkbox" /> : null}
          {option.dataset.icon ? <baywa-icon icon={option.dataset.icon}></baywa-icon> : null}
          {option.text}
        </li>
      );
    }
  }

  private renderOptionsList() {
    const filteredOptions = this.optionsState.filter(optionEl => this.filterOption(optionEl));
    return (
      <ul class="options" ref={(el: HTMLUListElement) => (this.optionListEl = el)}>
        {filteredOptions.map((option, idx) => this.renderOptionItem(option, idx))}
      </ul>
    );
  }

  private renderSearchInput() {
    if (this.searchable) {
      return (
        <baywa-input
          name="option-search"
          onInputRef={({ detail: el }) => (this.inputEl = el)}
          onInput={() => {
            this.handleFilter();
          }}
        >
          <div class="icon-search">
            <baywa-icon icon="icon-ebusiness-search"></baywa-icon>
          </div>
        </baywa-input>
      );
    }
    return null;
  }

  render() {
    return (
      <Host>
        <div class="inner">
          {this.renderSearchInput()}
          {this.renderOptionsList()}
        </div>
      </Host>
    );
  }
}
